import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { 
  Users, UserPlus, Calendar, TrendingUp, TrendingDown,
  DollarSign, Briefcase, Target, Award, Activity,
  BarChart3, PieChart, Clock, AlertCircle, CheckCircle,
  Building, Package, Truck, ShoppingCart, Settings,
  ArrowRight, Bell, FileText, Layers, UserCheck
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function AdminDashboard({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const recentActivities = [];

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [dashboardRes, hrRes, financeRes] = await Promise.all([
        axios.get(`${API}/api/dashboard/stats`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/api/dashboard/hr-summary`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/api/dashboard/finance-summary`, { headers }).catch(() => ({ data: {} }))
      ]);

      setStats({
        ...dashboardRes.data,
        hr: hrRes.data,
        finance: financeRes.data
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, bgColor, iconBg, link, trend, trendValue }) => (
    <Link to={link || '#'} className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-5 border border-gray-100">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-xl ${iconBg}`}>
          <Icon className="w-6 h-6" style={{ color: bgColor }} />
        </div>
        <div className="flex-1">
          <p className="text-sm text-gray-500 mb-0.5">{title}</p>
          <div className="flex items-baseline gap-2">
            <h3 className="text-2xl font-bold" style={{ color: '#1a1f3a' }}>{value}</h3>
            {trend && (
              <span className={`text-xs font-medium flex items-center ${trend === 'up' ? 'text-green-600' : 'text-red-500'}`}>
                {trend === 'up' ? <TrendingUp className="w-3 h-3 mr-0.5" /> : <TrendingDown className="w-3 h-3 mr-0.5" />}
                {trendValue}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400">{subtitle}</p>
        </div>
      </div>
    </Link>
  );

  const ModuleCard = ({ title, icon: Icon, stats, link, color }) => (
    <Link to={link} className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-5 border border-gray-100">
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <h3 className="font-semibold text-gray-800">{title}</h3>
      </div>
      <div className="space-y-2">
        {stats.map((stat, idx) => (
          <div key={idx} className="flex justify-between items-center">
            <span className="text-sm text-gray-500">{stat.label}</span>
            <span className="text-sm font-semibold text-gray-800">{stat.value}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-3 border-t">
        <span className="text-sm text-indigo-600 font-medium flex items-center gap-1">
          View Details <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </Link>
  );

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#2b3380' }}></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#1a1f3a' }}>Admin Dashboard</h1>
            <p className="text-gray-500">Welcome back, {user?.first_name || 'Admin'}</p>
          </div>
          <div className="flex gap-3">
            <Link to="/settings" className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Settings
            </Link>
            <Link to="/reports" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Reports
            </Link>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Total Employees"
            value={stats?.total_employees || 0}
            subtitle={`${stats?.new_hires_this_month || 0} new this month`}
            icon={Users}
            bgColor="#2b3380"
            iconBg="bg-indigo-50"
            link="/employees"
          />
          <StatCard
            title="Present Today"
            value={stats?.present_today || 0}
            subtitle={`${stats?.attendance_percentage || 0}% attendance`}
            icon={UserCheck}
            bgColor="#4caf50"
            iconBg="bg-green-50"
            link="/attendance"
          />
          <StatCard
            title="Open Positions"
            value={stats?.open_positions || 0}
            subtitle={`${stats?.total_candidates || 0} candidates`}
            icon={Briefcase}
            bgColor="#7e57c2"
            iconBg="bg-purple-50"
            link="/recruitment/jobs"
          />
          <StatCard
            title="Pending Approvals"
            value={stats?.pending_leaves || 0}
            subtitle="Requires action"
            icon={AlertCircle}
            bgColor="#ff9800"
            iconBg="bg-orange-50"
            link="/approvals"
          />
        </div>

        {/* Module Overview */}
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Module Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <ModuleCard
            title="HR & People"
            icon={Users}
            color="bg-indigo-600"
            link="/employees"
            stats={[
              { label: 'Departments', value: stats?.hr?.departments || 0 },
              { label: 'Leave Types', value: stats?.hr?.leave_types || 0 },
              { label: 'Holidays', value: stats?.hr?.holidays || 0 }
            ]}
          />
          <ModuleCard
            title="Recruitment"
            icon={Target}
            color="bg-purple-600"
            link="/recruitment/jobs"
            stats={[
              { label: 'Active Jobs', value: stats?.open_positions || 0 },
              { label: 'Candidates', value: stats?.total_candidates || 0 },
              { label: 'This Month Hires', value: stats?.new_hires_this_month || 0 }
            ]}
          />
          <ModuleCard
            title="Finance"
            icon={DollarSign}
            color="bg-green-600"
            link="/finance"
            stats={[
              { label: 'Pending Demands', value: stats?.finance?.pending_demands || 0 },
              { label: 'Suppliers', value: stats?.finance?.total_suppliers || 0 },
              { label: 'Pending GRNs', value: stats?.finance?.pending_grns || 0 }
            ]}
          />
          <ModuleCard
            title="Operations"
            icon={Package}
            color="bg-teal-600"
            link="/trc/dashboard"
            stats={[
              { label: 'Branches', value: stats?.hr?.branches || 0 },
              { label: 'Products', value: '—' },
              { label: 'Active Orders', value: '—' }
            ]}
          />
        </div>

        {/* Activity & Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Activity */}
          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-800">Recent Activity</h2>
              <Link to="/activity-log" className="text-sm text-indigo-600 hover:underline">View All</Link>
            </div>
            <div className="space-y-3">
              {recentActivities.length > 0 ? (
                recentActivities.map((activity, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className={`p-2 rounded-lg ${activity.color}`}>
                      <activity.icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-700">{activity.message}</p>
                      <p className="text-xs text-gray-400">{activity.time}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-6 text-center bg-gray-50 rounded-lg">
                  <Activity className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm text-gray-500">No activity recorded yet</p>
                  <p className="text-xs text-gray-400 mt-1">Real activity will appear here as your team starts using ZOBOX ERP.</p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
            <h2 className="font-semibold text-gray-800 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 gap-3">
              <Link to="/onboarding/new" className="flex items-center gap-3 p-4 bg-indigo-50 rounded-xl hover:bg-indigo-100 transition-colors">
                <UserPlus className="w-5 h-5 text-indigo-600" />
                <span className="text-sm font-medium text-gray-700">Add Employee</span>
              </Link>
              <Link to="/recruitment/jobs" className="flex items-center gap-3 p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors">
                <Briefcase className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-medium text-gray-700">Post Job</span>
              </Link>
              <Link to="/payroll" className="flex items-center gap-3 p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors">
                <DollarSign className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-gray-700">Run Payroll</span>
              </Link>
              <Link to="/approvals" className="flex items-center gap-3 p-4 bg-orange-50 rounded-xl hover:bg-orange-100 transition-colors">
                <CheckCircle className="w-5 h-5 text-orange-600" />
                <span className="text-sm font-medium text-gray-700">Approvals</span>
              </Link>
              <Link to="/roles" className="flex items-center gap-3 p-4 bg-teal-50 rounded-xl hover:bg-teal-100 transition-colors">
                <Award className="w-5 h-5 text-teal-600" />
                <span className="text-sm font-medium text-gray-700">Manage Roles</span>
              </Link>
              <Link to="/hr-settings" className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors">
                <Settings className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">HR Settings</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default AdminDashboard;
